import { FeatureTeamEvent, isPwaMode, isWeb, TrackingEventType, useAnalytics } from '@capsicum/common';
import { useCallback } from 'react';

import { type MigrateableFanToken } from '../models';

type UseFTMigrationAnalyticsProps = {
	tokens: Array<MigrateableFanToken>;
};

const FEATURE_NAME = 'ft_migration';

export const useV2FTMigrationAnalytics = ({ tokens }: UseFTMigrationAnalyticsProps) => {
	const analytics = useAnalytics();

	const triggerFAQFTMigrationEvent = useCallback(
		() =>
			analytics.sendEvent(TrackingEventType.track, 'GA::modal_faq_ft_migration', {
				feature: FEATURE_NAME,
				feature_team: FeatureTeamEvent.PFFT,
				is_pwa_mode: isWeb() ? isPwaMode() : undefined
			}),
		[analytics]
	);

	const triggerUpgradeFTMigrationEvent = useCallback(
		() =>
			analytics.sendEvent(TrackingEventType.track, 'GA::trigger_upgrade_ft_migration', {
				feature: FEATURE_NAME,
				feature_team: FeatureTeamEvent.PFFT,
				is_pwa_mode: isWeb() ? isPwaMode() : undefined
			}),
		[analytics]
	);

	const triggerConfirmUpgradeFTMigrationEvent = useCallback(() => {
		if (!tokens) {
			throw new Error("when triggering 'GA::confirm_upgrade_ft_migration' you need to specify the 'tokens' prop");
		}

		analytics.sendEvent(TrackingEventType.track, 'GA::confirm_upgrade_ft_migration', {
			feature: FEATURE_NAME,
			feature_team: FeatureTeamEvent.PFFT,
			ft_amount: tokens.reduce((total, token) => total + Number(token.amount), 0),
			is_pwa_mode: isWeb() ? isPwaMode() : undefined
		});
	}, [tokens, analytics]);

	const triggerModalUpgradeFTMigrationEvent = useCallback(
		() =>
			analytics.sendEvent(TrackingEventType.track, 'GA::modal_upgrade_ft_migration', {
				feature: FEATURE_NAME,
				feature_team: FeatureTeamEvent.PFFT
			}),
		[analytics]
	);

	const triggerOptOutMigrationEvent = useCallback(
		() =>
			analytics.sendEvent(TrackingEventType.track, 'GA::opted_out_ft_migration', {
				feature: FEATURE_NAME,
				feature_team: FeatureTeamEvent.PFFT,
				is_pwa_mode: isWeb() ? isPwaMode() : undefined
			}),
		[analytics]
	);

	return {
		triggerConfirmUpgradeFTMigrationEvent,
		triggerFAQFTMigrationEvent,
		triggerModalUpgradeFTMigrationEvent,
		triggerOptOutMigrationEvent,
		triggerUpgradeFTMigrationEvent
	};
};
